import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../config/authConfig";
import { logger } from "./logger";

// Create a singleton instance to be used throughout the application
export const msalInstance = new PublicClientApplication(msalConfig);

// Track initialization state
let isInitialized = false;
let initError: Error | null = null;
let isRedirectHandlerRunning = false;

// Handle any pending redirects before doing any other auth operations
export const handlePendingRedirect = async () => {
  if (isRedirectHandlerRunning) {
    logger.debug("Redirect handler already running");
    return;
  }
  
  try {
    isRedirectHandlerRunning = true;
    logger.debug("Checking for pending redirect response");
    
    // Handle any pending redirect response
    const response = await msalInstance.handleRedirectPromise();
    
    if (response) {
      logger.debug("Successfully processed redirect response");
      // Only log details in development mode
      if (process.env.NODE_ENV !== 'production') {
        logger.debug("Redirect response details:", {
          tokenType: response.tokenType,
          account: response.account?.username || 'unknown'
        });
      }
    } else {
      logger.debug("No pending redirect response");
    }
    
    return response;
  } catch (error) {
    logger.error("Error handling redirect");
    if (process.env.NODE_ENV !== 'production' && error instanceof Error) {
      logger.error(error.message);
    }
    throw error;
  } finally {
    isRedirectHandlerRunning = false;
  }
};

// This function ensures MSAL is initialized before the app fully loads
export const initializeMsal = async () => {
  try {
    if (isInitialized) {
      return msalInstance;
    }
    
    logger.debug("Initializing MSAL application...");
    
    // First, handle any pending redirects
    await handlePendingRedirect();
    
    // Then initialize MSAL
    await msalInstance.initialize();
    logger.debug("MSAL successfully initialized");
    
    isInitialized = true;
    initError = null;
    
    return msalInstance;
  } catch (error) {
    logger.error("Failed to initialize MSAL");
    if (process.env.NODE_ENV !== 'production' && error instanceof Error) {
      logger.error(error.message);
    }
    initError = error instanceof Error ? error : new Error(String(error));
    throw error;
  }
};

// Function to check if MSAL is initialized
export const isMsalInitialized = () => {
  return isInitialized;
};

// Function to get initialization error
export const getMsalInitError = () => {
  return initError;
};

// Helper to check for and clear interaction-in-progress state
export const clearInteractionInProgress = () => {
  try {
    // Check localStorage for interaction in progress flags
    const inProgressKeys = Object.keys(localStorage)
      .filter(key => key.includes('interaction.status') && localStorage.getItem(key) === 'in_progress');
    
    if (inProgressKeys.length > 0) {
      logger.debug("Found interaction in progress flags, clearing...");
      inProgressKeys.forEach(key => localStorage.removeItem(key));
      return true;
    }
    
    return false;
  } catch (e) {
    logger.error("Error clearing interaction state");
    return false;
  }
};

// Helper function to manage auth operations and prevent conflicts
export const runSafeAuthOperation = async (operation: () => Promise<any>) => {
  // First check if we can clear any stuck interaction flags
  clearInteractionInProgress();
  
  // Handle any pending redirects first
  await handlePendingRedirect();
  
  // Now it's safe to run the operation
  return operation();
}; 